import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { HttpStatusCode } from "axios";
import { rejectAxiosError } from "../rtkErrorNotifications";
import { notifyApiCallFailed } from "../notifications/notificationsSlice";

export const searchRenewals = createAsyncThunk(
  "renewalsSearch/search",
  async (
    {
      include_cancelled,
      include_completed,
      include_not_sent,
      include_sent,
      search_type,
      search_criteria,
    },
    api
  ) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.get(baseUrl + "/renewals", {
        params: {
          include_cancelled: include_cancelled,
          include_completed: include_completed,
          include_not_sent: include_not_sent,
          include_sent: include_sent,
          search_type: search_type,
          search_criteria: search_criteria,
        },
        ...config,
      });
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const fetchRenewalForm = createAsyncThunk(
  "renewalsSearch/fetchRenewalForm",
  async ({ id }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.get(`${baseUrl}/renewals/${id}`, config);
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const updateRenewalCoverageDate = createAsyncThunk(
  "renewalsSearch/updateRenewalCoverageDate",
  async ({ id, coverageDate }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.patch(
        `${baseUrl}/renewals/${id}/update_coverage_date/`,
        {
          coverage_date: coverageDate,
        },
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const updateRenewalStatus = createAsyncThunk(
  "renewalsSearch/updateRenewalStatus",
  async ({ id, status }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.patch(
        `${baseUrl}/renewals/${id}/update_status/`,
        {
          status: status,
        },
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const updateRenewalCustomerCode = createAsyncThunk(
  "renewalsSearch/updateRenewalCustomerCode",
  async ({ id, customerCode }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.patch(
        `${baseUrl}/renewals/${id}/update_customer_code/`,
        {
          customer_code: customerCode,
        },
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const cancelRenewal = createAsyncThunk(
  "renewalsSearch/cancelRenewal",
  async ({ id, reason }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.patch(
        `${baseUrl}/renewals/${id}/cancel/`,
        {
          cancellation_reason: reason,
        },
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const fetchRenewalEmailPreview = createAsyncThunk(
  "renewalsSearch/fetchRenewalEmailPreview",
  async ({ id }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.get(
        `${baseUrl}/renewals/${id}/email_preview`,
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const fetchEmailSignature = createAsyncThunk(
  "renewalsSearch/fetchEmailSignature",
  async (_, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.get(`${baseUrl}/settings`, config);
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const updateEmailSignature = createAsyncThunk(
  "renewalsSearch/updateEmailSignature",
  async ({ email_signature }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;
    try {
      let config = {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const response = await axios.patch(
        `${baseUrl}/settings`,
        {
          email_signature: email_signature,
        },
        config
      );
      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const sendRenewalEmail = createAsyncThunk(
  "renewalsSearch/sendRenewalEmail",
  async (
    { id, attachment, subject, body, includePreviousApplication = true },
    api,
  ) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;

    try {
      const token = localStorage.getItem("token");

      const formData = new FormData();
      if (attachment) formData.append("attachment", attachment);
      // wording as edited in the compose view; the backend falls back to the template
      if (subject) formData.append("subject", subject);
      if (body) formData.append("body", body);
      formData.append(
        "include_previous_application",
        includePreviousApplication ? "true" : "false",
      );

      const response = await axios.post(
        `${baseUrl}/renewals/${id}/send_email/`,
        formData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === HttpStatusCode.Ok) {
        return response.data;
      }

      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  }
);

export const resolveRenewalEmailDelivery = createAsyncThunk(
  "renewalsSearch/resolveRenewalEmailDelivery",
  async ({ id, resolution }, api) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;

    try {
      const response = await axios.post(
        `${baseUrl}/renewals/${id}/resolve_email_delivery`,
        { resolution },
        { headers: { Authorization: localStorage.getItem("token") } },
      );
      if (response.status === HttpStatusCode.Ok) return response.data;
      throw new Error();
    } catch (error) {
      return rejectAxiosError(error, api);
    }
  },
);

export const fetchQuotePdf = createAsyncThunk(
  "renewalsSearch/fetchQuotePdf",
  async ({ id }, { rejectWithValue, dispatch }) => {
    const baseUrl =
      process.env.REACT_APP_USE_LOCAL_API_URL === "true"
        ? process.env.REACT_APP_LOCAL_API_URL
        : process.env.REACT_APP_EXTERNAL_API_URL;

    try {
      let config = {
        responseType: "blob",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      };
      const { data, status } = await axios.get(
        `${baseUrl}/private_aviation/quotes/${id}/serve_pdf`,
        config
      );
      if (status !== HttpStatusCode.Ok) throw new Error("bad status");
      return URL.createObjectURL(data);
    } catch (err) {
      dispatch(notifyApiCallFailed());
      return rejectWithValue(err.message);
    }
  }
);
